import type { Player, Character, TurnPhase } from '../../types';
import { ASSETS } from '../../data/assets';
import styles from './DiceRoller.module.css';

// 诊断：限流日志（模块级变量，避免刷屏）
let _lastDiceLog = 0;

interface Props {
  diceResult: [number, number] | null;
  turnPhase: TurnPhase;
  currentPlayer: Player | undefined;
  currentChar: Character | undefined;
  moveSteps: number;
  onRoll: () => void;
  /** 联机模式：当前是否轮到本地玩家操作 */
  isMyTurn?: boolean;
  /** 联机模式：是否正在等待其他玩家 */
  isWaitingForOthers?: boolean;
  /** 联机模式：掷骰子倒计时秒数（0=不显示） */
  countdown?: number;
}

export default function DiceRoller({ diceResult, turnPhase, currentPlayer, moveSteps, onRoll, isMyTurn, isWaitingForOthers, countdown }: Props) {
  const dice1 = diceResult?.[0] || 0;
  const dice2 = diceResult?.[1] || 0;
  const total = dice1 + dice2;
  // 可以掷骰子：phase=rolling + 非Bot + 联机下为本回合+ 非等待他人
  const canRoll = turnPhase === 'rolling' && !currentPlayer?.isBot && isMyTurn !== false;
  // 诊断：每5秒打印一次状态（避免刷屏）
  const now = Date.now();
  if (now - _lastDiceLog > 5000) {
    _lastDiceLog = now;
    console.log('[DICE] DiceRoller render: turnPhase=', turnPhase, 'isMyTurn=', isMyTurn,
      'isWaiting=', isWaitingForOthers, 'canRoll=', canRoll,
      'cp=', currentPlayer?.name, 'cp.isBot=', currentPlayer?.isBot,
      'diceResult=', diceResult);
  }

  // 联机等待中
  if (isWaitingForOthers) {
    return (
      <div className={styles.container}>
        <div className={styles.waitingBanner}>
          <span className={styles.waitingIcon}>⏳</span>
          <span className={styles.waitingText}>等待其他玩家操作...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 联机掷骰倒计时条 */}
      {countdown && countdown > 0 && canRoll && (
        <div className={styles.countdownBar}>
          <div
            className={styles.countdownFill}
            style={{ width: `${(countdown / 30) * 100}%` }}
          />
          <span className={styles.countdownText}>{countdown}s</span>
        </div>
      )}

      <div className={styles.diceDisplay}>
        <div className={`${styles.die} ${diceResult ? styles.dieRolled : ''}`}>
          {dice1 > 0 ? (
            <img src={ASSETS.dice.face(dice1)} alt={`骰子${dice1}`} className={styles.diceImg} />
          ) : (
            <img src={ASSETS.dice.face(1)} alt="骰子占位" className={styles.diceImgPlaceholder} />
          )}
        </div>
        <div className={`${styles.die} ${diceResult ? styles.dieRolled : ''}`}>
          {dice2 > 0 ? (
            <img src={ASSETS.dice.face(dice2)} alt={`骰子${dice2}`} className={styles.diceImg} />
          ) : (
            <img src={ASSETS.dice.face(6)} alt="骰子占位" className={styles.diceImgPlaceholder} />
          )}
        </div>
      </div>

      {diceResult && (
        <div className={styles.resultText}>
          <span>点数: {total}</span>
          {moveSteps > 0 && <span className={styles.steps}>前进{total}步</span>}
        </div>
      )}

      <button
        className={`${styles.rollBtn} ${canRoll ? styles.rollBtnActive : styles.rollBtnDisabled}`}
        onClick={onRoll}
        disabled={!canRoll}
      >
        <img src={ASSETS.ui.btnDice} alt="掷骰子" className={styles.rollBtnImg} />
        <span className={styles.rollBtnText}>
          {currentPlayer?.isBot ? '等待Bot...'
            : !isMyTurn && currentPlayer ? `等待 ${currentPlayer.name}...`
            : canRoll && countdown && countdown > 0 ? `掷骰子 (${countdown}s)`
            : canRoll ? '掷骰子'
            : '等待中'}
        </span>
      </button>
    </div>
  );
}
